/**
 * 공통 util
 * @module shared/utils/CommonUtil.ts
 * @author Lian
 */
import axios from 'axios';
import ExcelJS from 'exceljs';

import { ModalInfo } from '@/shared/components/Modal.tsx';
import useAuthStore from '@/shared/store/UseAuthStore.ts';
import useCustomDataStore from '@/shared/store/UseCustomDataStore.ts';
import { CryptoManager } from '@/shared/utils/crypto/CryptoManager.ts';
import logger from '@/shared/utils/Logger.ts';
import useCodeStore from '../store/UseCodeStore';
import { getAuthState } from '../store/UseAppAuthStore';
import useBottomSheetStore from '../store/UseBottomSheetStore';
import { useMemo } from 'react';

const matchesUrl = ['user/', 'public/', 'board/', 'customize/', 'auth/', 'manage/code'];

/**
 * 업로드용 axios
 * @author Lian
 */
const uploadApi = axios.create({
  headers: {
    'Content-type': 'multipart/form-data',
  },
});

/**
 * axios 요청 관리용 map
 * @author Lian
 */
const requestControllerMap = new Map<string, AbortController>();

/**
 * 값이 `null` 또는 `undefined`가 아닌지 검사합니다.
 * @param v - 확인하려는 값
 * @returns 빈 값 여부 (true: 빈 값 아님/false: 빈 값)
 * @author Olive
 */
export const vDefined = <T>(v: T | null | undefined): v is T => v !== undefined && v !== null;

/**
 * 전달받은 값이 `null` 또는 `undefined` 일 때, 기본 값을 적용합니다.
 * @param v - 확인하려는 값
 * @param fallback - 기본 값
 * @returns 전달 받은 값 또는 기본 값
 * @author Olive
 */
export const vDefaultValue = <T>(v: T | null | undefined, fallback: T): T =>
  vDefined(v) ? v! : fallback;

/**
 * 문자 암호화
 * @param string data - 암호화 필요 파라미터
 * @author Lian
 */
export const vEncrypt = async (plain: string): Promise<string> => {
  const key = await CryptoManager.getKey();
  const cipher = key.encrypt(plain);
  if (!cipher) {
    throw new Error('암호화 실패');
  }
  return cipher;
};

const vLoginCheck = (pathname: string) => {
  const allow = matchesUrl.some(url => pathname.startsWith(url));
  if (!allow) {
    const { userId } = useAuthStore.getState();

    if (userId === null || userId === '') {
      ModalInfo('세션이 만료되었습니다. 다시 로그인해주세요.', () => {
        window.location.href = '/manage/';
      });
      return true;
    }
  }

  return false;
};

/**
 * ajax 전달 data 자료구조 인터페이스
 * @author Lian
 */
interface AjaxData {
  /**
   * qid: CRUD 에 필요한 query id
   */
  qid?: string;

  /**
   * json 형태의 서버에 전달하는 data
   */
  [key: string]: any;
}

/**
 * 서버통신 기능
 * @param string type - post | get
 * @param string url - 서버 url
 * @param AjaxData data - 통신할 자료 json
 * @param Function successFunction - 성공 콜백 함수
 * @param Function errorFunction - 에러 콜백 함수
 * @author lian
 */
export const vAjax = async <T>(
  type: 'get' | 'post',
  url: string,
  data: AjaxData,
  successFunction?: (res: AxiosResponse<T>) => any,
  errorFunction?: (err: any) => any,
): Promise<T | void> => {
  if (vLoginCheck(url)) return;

  const fullUrl = `/api/${url}`;

  /**
   * fullUrl에 해당하는 이전 요청이 있을 시 abort
   */
  const existingController = requestControllerMap.get(fullUrl);
  if (existingController) {
    existingController.abort();
  }

  const axiosController = new AbortController();
  requestControllerMap.set(fullUrl, axiosController);

  const cloneData = { ...data };

  if (vDefined(data.qid)) {
    cloneData.qid = await vEncrypt(data.qid as string);
  }

  const { token, userId } = useAuthStore.getState();

  let headers: Record<string, string> = {};

  if (token) {
    cloneData.userId = userId;
    headers = { Authorization: `Bearer ${token}` };
  }

  const axiosConfig: any = {
    method: type.toLowerCase(),
    url: fullUrl,
    headers,
    withCredentials: true,
    signal: axiosController.signal,
  };

  // GET이면 params, POST면 data
  if (type === 'get') {
    axiosConfig.params = cloneData;
  } else {
    axiosConfig.data = cloneData;
  }

  await axios(axiosConfig)
    .then(response => {
      if (successFunction) successFunction(response);
    })
    .catch(error => {
      if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
        return;
      }
      if (errorFunction) errorFunction(error);
    });
};

export const vAjaxDataServer = async (
  type: 'get' | 'post',
  url: string,
  successFunction?: Function,
  errorFunction?: Function,
): Promise<void> => {
  if (vLoginCheck(url)) return;
  const fullUrl = `/data/${url}`;

  const existingController = requestControllerMap.get(fullUrl);
  if (existingController) {
    existingController.abort();
  }

  const axiosController = new AbortController();
  requestControllerMap.set(fullUrl, axiosController);

  await axios({
    method: type.toLowerCase(),
    url: fullUrl,
    withCredentials: true,
    signal: axiosController.signal,
  })
    .then(response => {
      if (successFunction) successFunction(response);
    })
    .catch(error => {
      if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
        return;
      }
      if (errorFunction) errorFunction(error);
    });
};

export const vAjaxApi = async (
  type: 'get' | 'post',
  apiType: 'vworld' | 'seongnam',
  url: string,
  param: any,
  successFunction?: Function,
  errorFunction?: Function,
): Promise<void> => {
  if (vLoginCheck(url)) return;
  const fullUrl = `/${apiType}/${url}`;

  const existingController = requestControllerMap.get(fullUrl);
  if (existingController) {
    existingController.abort();
  }

  const axiosController = new AbortController();
  requestControllerMap.set(fullUrl, axiosController);

  const axiosConfig: any = {
    method: type.toLowerCase(),
    url: fullUrl,
    withCredentials: true,
    signal: axiosController.signal,
  };

  // GET이면 params, POST면 data
  if (type === 'get') {
    axiosConfig.params = param;
  } else {
    axiosConfig.data = param;
  }

  await axios(axiosConfig)
    .then(response => {
      if (successFunction) successFunction(response);
    })
    .catch(error => {
      if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
        return;
      }
      if (errorFunction) errorFunction(error);
    });
};

export const vUploadDataServer = (
  url: string,
  params: object,
  successFunction?: Function,
  errorFunction?: Function,
) => {
  if (vLoginCheck(url)) return;
  const fullUrl = `/data/${url}`;

  return uploadApi
    .post(fullUrl, params, { withCredentials: true })
    .then(response => {
      if (successFunction) successFunction(response);
    })
    .catch(error => {
      if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
        return;
      }
      if (errorFunction) errorFunction(error);
    });
};

/**
 * 시스템코드 조회
 * @param upperCode 상위코드
 * @returns SystemCodeInfo[]
 */
export const vGetSystemCode = (upperCode: string) => {
  // const { systemCodes } = useCodeStore.getState();
  // return [systemCodes.filter(code => code.upSysCd === upperCode)];
  return [];
};

/**
 * 시스템코드 조회
 * @param upperCode 상위코드
 * @returns SystemCodeInfo[]
 */
export const vGetCode = (upperCode: string) => {
  const codes = useCodeStore(s => s.codes);

  // 가공된 options 형태로 바로 반환하도록 구성 가능
  return useMemo(() => {
    return codes
      .filter(code => code.groupCode === upperCode)
      .map(code => ({ code: code.code, codeName: code.codeName }));
  }, [codes, upperCode]);
};
/**
 * 입력한 Byte 값을 단위와 함께 변환합니다.
 * @param inputedBytes - 입력한 Byte 값
 * @param decimals - 소수점 단위 (default = 2)
 * @returns 변환된 단위값
 * @author Olive
 */
export const vFormatBytes = (inputedBytes: number | string, decimals = 2) => {
  let bytes = 0;
  if (typeof inputedBytes === 'string') {
    bytes = Number(inputedBytes.replaceAll(',', ''));
  } else {
    bytes = Number(inputedBytes);
  }
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / k ** i).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * RGBA 색상 정보를 나타내는 인터페이스
 * @author Andy
 */
interface Color {
  red: number;
  green: number;
  blue: number;
  alpha: number;
}

/**
 * Color 객체를 CSS 색상 문자열 및 투명도(%)로 변환합니다.
 * @param color - red, green, blue, alpha 값을 포함하는 Color 객체 (0.0 ~ 1.0 범위)
 * @returns 변환된 CSS 색상 문자열과 alpha 값
 * @author Andy
 */
export const vColorToCssString = (color: Color): { color: string; alpha: number } => {
  function floatToByte(number: number) {
    return number === 1.0 ? 255 : number * 256 || 0;
  }

  const r = floatToByte(color.red).toString(16).padStart(2, '0');
  const g = floatToByte(color.green).toString(16).padStart(2, '0');
  const b = floatToByte(color.blue).toString(16).padStart(2, '0');

  return {
    color: `#${r}${g}${b}`,
    alpha: Math.round((1.0 - color.alpha) * 100),
  };
};

/**
 * 데이터명에 맞는 레이어 속성 정보를 반환합니다.
 * @param type 레이어 유형 (facility: 시설물)
 * @param dataName 데이터 명
 * @returns {Promise<FacilityLayerData[] | undefined>}
 */
export const vGetLayerData = async (
  type: string,
  dataName: string,
): Promise<FacilityLayerData[] | undefined> => {
  if (type === 'facility') {
    const { facilityLayer } = useCustomDataStore.getState();
    const layer = facilityLayer.find(l => l.engDataNm.toLowerCase() === dataName.toLowerCase());

    if (!vDefined(layer)) {
      return;
    }

    if (layer.properties && layer.properties.length > 0) {
      return layer.properties;
    }

    return await vSetLayerData(type, dataName);
  }
};

/**
 * 타입과 데이터명에 맞는 레이어 속성 정보를 설정합니다
 * @param type 레이어 유형 (facility: 시설물)
 * @param dataName 데이터 명
 * @returns {Promise<FacilityLayerData[] | undefined>}
 */
export const vSetLayerData = async (type: string, dataName: string) => {
  const { userId } = useAuthStore.getState();
  const { facilityLayer, setFacilityLayer } = useCustomDataStore.getState();

  if (type === 'facility') {
    let layerData = [] as FacilityLayerData[];
    await vAjax(
      'post',
      'com/qid/list',
      {
        qid: 'layer.selectFieldList',
        userId: userId,
        dataName,
      },
      (result: { data: { result: FacilityLayerData[] } }) => {
        if (vDefined(result) && vDefined(result.data)) {
          layerData = result.data.result;

          layerData.forEach(item => {
            // Oracle의 경우 domainList 컬럼이 JSON 문자열 타입
            // Postgresql의 경우 domainList 컬럼이 Object 타입
            if (typeof item.domainList === 'string') {
              item.domainList = JSON.parse(item.domainList);
            } else if (typeof item.domainList === 'object') {
              if (item.domainList.value && typeof item.domainList.value === 'string') {
                item.domainList.value = JSON.parse(item.domainList.value);
              }
            }
          });

          const updatedLayers = facilityLayer.map(l =>
            l.engDataNm.toLowerCase() === dataName.toLowerCase()
              ? { ...l, properties: layerData }
              : l,
          );
          setFacilityLayer(updatedLayers);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );

    return layerData;
  }
};

/**
 * a / b의 나머지를 b의 부호에 따라 반환합니다.
 * @param a Dividend.
 * @param b Divisor.
 * @return Modulo.
 */
export const modulo = (a: number, b: number): number => {
  const r = a % b;
  return r * b < 0 ? r + b : r;
};

/**
 * 위경도 십진법을 도분초로 변환하여 반환합니다.
 * @param degrees 경도 또는 위도
 * @param fractionDigits 소숫점 자리수
 * @returns 도분초
 */
export const degreesToStringHDMS = (degrees: number, fractionDigits?: number): string => {
  const normalizedDegrees = modulo(degrees + 180, 360) - 180;
  const x = Math.abs(3600 * normalizedDegrees);
  const dflPrecision = fractionDigits || 0;
  const precision = 10 ** dflPrecision;

  let deg = Math.floor(x / 3600);
  let min = Math.floor((x - deg * 3600) / 60);
  let sec = x - deg * 3600 - min * 60;
  sec = Math.ceil(sec * precision) / precision;

  if (sec >= 60) {
    sec = 0;
    min += 1;
  }

  if (min >= 60) {
    min = 0;
    deg += 1;
  }

  return deg + '\u00b0 ' + min + '\u2032 ' + sec + '\u2033';
};

/**
 * 엑셀다운로드
 * @param fileName - 파일명
 * @param excelData - 엑셀다운로드 데이터
 */
export const excelDownload = async (fileName: string, excelData: any[]) => {
  const now = new Date();

  const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;

  const excelFileName = `${fileName}_${timestamp}.xlsx`;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet1');

  const columns = Object.keys(excelData[0] || {}).map(key => ({
    header: key,
    key,
    width: 20,
  }));
  worksheet.columns = columns;

  worksheet.getRow(1).eachCell(cell => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '4F81BD' },
    };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  excelData.forEach(item => {
    worksheet.addRow(item);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/octet-stream' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName ? excelFileName : 'excel.xlsx';
  link.click();
};

/**
 * shape 파일 다운로드 함수
 * @param tableName - 테이블명
 * @author Lian
 */
export const shapeFileDownLoad = async (tableName: string) => {
  const { token } = useAuthStore.getState();

  if (vDefined(token)) {
    const response = await fetch(`/api/manage/data/shape-file-download?tableName=${tableName}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Shapefile 다운로드 실패');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${tableName}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  }
};

/**
 * 시스템 로그 적재 함수
 * @author Andy
 */
export const writeSystemLog = async (logData: LogData) => {
  const { userId } = useAuthStore.getState();

  await vAjax(
    'post',
    'log/write',
    {
      userId,
      logData,
    },
    (response: AxiosResponse<CommonResult>) => {
      if (response.data.code !== 200) {
        logger.error(response.data.message);
      }
    },
    (error: any) => {
      logger.error(error);
    },
  );
};

export const getBackEndUrl = () => {
  const VITE_BACK_END_URL = import.meta.env?.VITE_BACK_END_URL;
  return `${VITE_BACK_END_URL}`;
};

export const getUploadsUrl = () => {
  const VITE_UPLOADS_URL = import.meta.env?.VITE_UPLOADS_URL;
  return `${VITE_UPLOADS_URL}`;
};

/**
 * 로그인 체크 함수
 * 특정 URL은 로그인 체크를 하지 않음
 */
const vAppLoginCheck = (pathname: string): boolean => {
  const allow = matchesUrl.some(url => pathname.startsWith(url));
  const { accessToken } = getAuthState();
  const { openSheet } = useBottomSheetStore.getState();

  if (!allow) {
    const token = localStorage.getItem('accessToken');

    if (!token) {
      openSheet('message', {
        type: 'INFO',
        message: '로그인 한지 오래 되어 다시 로그인이 필요합니다.',
        onConfirm: () => {
          window.location.href = '/login';
        },
      });
      return true;
    }
  }

  return false;
};

/**
 * Refresh Token으로 새 Access Token 발급
 */
const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      throw new Error('Refresh token not found');
    }

    const response = await axios.post(`/api/auth/user/refresh`, {
      refreshToken,
    });

    const { accessToken, refreshToken: newRefreshToken } = response.data;

    // 새 토큰 저장
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', newRefreshToken);

    console.log('Token refreshed successfully');
    return accessToken;
  } catch (error) {
    console.error('Token refresh failed:', error);

    // Refresh Token도 만료됨 - 로그아웃 처리
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    alert('세션이 만료되었습니다. 다시 로그인해주세요.');
    window.location.href = '/login';

    return null;
  }
};

/**
 * Refresh Token으로 새 App 용 Access Token 발급
 */
const refreshAppAccessToken = async (): Promise<string | null> => {
  const { updateTokens, refreshToken, logout } = getAuthState();
  const { openSheet } = useBottomSheetStore.getState();
  try {
    if (!refreshToken) {
      throw new Error('Refresh token not found');
    }

    const response = await axios.post(`/api/auth/user/refresh`, {
      refreshToken,
    });

    const { accessToken, refreshToken: newRefreshToken } = response.data;

    updateTokens(accessToken, newRefreshToken);
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', newRefreshToken);

    console.log('Token refreshed successfully');
    return accessToken;
  } catch (error) {
    console.error('Token refresh failed:', error);

    // Refresh Token도 만료됨 - 로그아웃 처리
    logout();

    openSheet('message', {
      type: 'INFO',
      message: '로그인 한지 오래 되어 다시 로그인이 필요합니다.',
    });
    window.location.href = '/login';

    return null;
  }
};

/**
 * App Ajax 요청 함수 (localStorage 기반)
 *
 * @param type - 'get' 또는 'post'
 * @param url - API 엔드포인트 (예: 'members/me')
 * @param data - 요청 데이터
 * @param successFunction - 성공 시 콜백
 * @param errorFunction - 에러 시 콜백
 * @returns Promise<T | void>
 */
export const vAppAjax = async <T>(
  type: 'get' | 'post',
  url: string,
  data: AjaxData = {},
  successFunction?: (res: AxiosResponse<T>) => any,
  errorFunction?: (err: any) => any,
): Promise<T | void> => {
  const { member, accessToken } = getAuthState();
  // 로그인 체크
  if (vAppLoginCheck(url)) return;

  const fullUrl = `/api/app/${url}`;

  /**
   * fullUrl에 해당하는 이전 요청이 있을 시 abort
   */
  const existingController = requestControllerMap.get(fullUrl);
  if (existingController) {
    existingController.abort();
  }

  const axiosController = new AbortController();
  requestControllerMap.set(fullUrl, axiosController);

  const cloneData = { ...data };

  // QID 암호화
  if (vDefined(data.qid)) {
    cloneData.qid = await vEncrypt(data.qid as string);
  }

  // localStorage에서 토큰 가져오기
  const userId = member?.memberId; // 필요한 경우

  let headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
    // if (userId) {
    //   cloneData.userId = userId;
    // }
  }

  const axiosConfig: any = {
    method: type.toLowerCase(),
    url: fullUrl,
    headers,
    withCredentials: true,
    signal: axiosController.signal,
  };

  // GET이면 params, POST면 data
  if (type === 'get') {
    axiosConfig.params = cloneData;
  } else {
    axiosConfig.data = cloneData;
  }

  try {
    const response = await axios(axiosConfig);

    // 요청 성공 - 컨트롤러 맵에서 제거
    requestControllerMap.delete(fullUrl);

    if (successFunction) {
      successFunction(response);
    }

    return response.data as T;
  } catch (error: any) {
    // 요청 취소된 경우 (중복 요청 방지)
    if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
      console.log('Request canceled:', fullUrl);
      return;
    }

    // 401 에러 (토큰 만료) - Refresh Token으로 재시도
    if (error.response?.status === 401 && !axiosConfig._retry) {
      console.log('Token expired, attempting refresh...');

      const newToken = await refreshAppAccessToken();

      if (newToken) {
        // 새 토큰으로 재시도
        axiosConfig._retry = true;
        axiosConfig.headers.Authorization = `Bearer ${newToken}`;

        try {
          const retryResponse = await axios(axiosConfig);
          requestControllerMap.delete(fullUrl);

          if (successFunction) {
            successFunction(retryResponse);
          }

          return retryResponse.data as T;
        } catch (retryError: any) {
          requestControllerMap.delete(fullUrl);

          if (errorFunction) {
            errorFunction(retryError);
          }

          throw retryError;
        }
      }

      return;
    }

    // 그 외 에러
    requestControllerMap.delete(fullUrl);

    if (errorFunction) {
      errorFunction(error);
    } else {
      // 기본 에러 처리
      console.error('Ajax Error:', error);

      if (error.response) {
        // 서버 응답이 있는 경우
        const message = error.response.data?.message || '요청 처리 중 오류가 발생했습니다.';
        alert(message);
      } else if (error.request) {
        // 요청은 보냈지만 응답이 없는 경우
        alert('서버와 연결할 수 없습니다. 네트워크를 확인해주세요.');
      } else {
        // 요청 설정 중 오류가 발생한 경우
        alert('요청 처리 중 오류가 발생했습니다.');
      }
    }

    throw error;
  }
};

/**
 * GET 요청 헬퍼 함수
 */
export const vGet = <T>(
  url: string,
  params?: AjaxData,
  successFunction?: (res: AxiosResponse<T>) => any,
  errorFunction?: (err: any) => any,
): Promise<T | void> => {
  return vAjax<T>('get', url, params || {}, successFunction, errorFunction);
};

/**
 * POST 요청 헬퍼 함수
 */
export const vPost = <T>(
  url: string,
  data?: AjaxData,
  successFunction?: (res: AxiosResponse<T>) => any,
  errorFunction?: (err: any) => any,
): Promise<T | void> => {
  return vAjax<T>('post', url, data || {}, successFunction, errorFunction);
};

/**
 * 로그아웃 함수
 */
export const vLogout = () => {
  // 토큰 제거
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userId');

  // 로그인 페이지로 이동
  window.location.href = '/login';
};

/**
 * 현재 로그인 상태 확인
 */
export const vIsLoggedIn = (): boolean => {
  const token = localStorage.getItem('accessToken');
  return !!token;
};

export const getFileSrc = (fileUrl: string) => {
  return new URL(`/assets/${fileUrl}`, window.location.origin).href;
};

/**
 * 닉네임 변경 모달을 띄우는 공통 함수
 */
export const openUpdateNicknameSheet = () => {
  const { openSheet } = useBottomSheetStore.getState();

  openSheet('update-nickname', { showHeader: true, title: '닉네임 변경' });
};

/**
 * 받침 유무에 따라 조사 반환
 * @param word - 한글 단어
 * @param withBatchim - 받침 있을 때 조사 (을, 이, 은, 과)
 * @param withoutBatchim - 받침 없을 때 조사 (를, 가, 는, 와)
 */
export const getParticle = (word: string, withBatchim: string, withoutBatchim: string): string => {
  if (!word) return withoutBatchim;

  const lastChar = word.charCodeAt(word.length - 1);

  // 한글 유니코드 범위: 0xAC00 ~ 0xD7A3
  if (lastChar < 0xac00 || lastChar > 0xd7a3) return withoutBatchim;

  // 종성 = (lastChar - 0xAC00) % 28
  // 0이면 받침 없음
  const hasBatchim = (lastChar - 0xac00) % 28 !== 0;

  return hasBatchim ? withBatchim : withoutBatchim;
};

/** 을/를 */
export const eulReul = (word: string) => `${word}${getParticle(word, '을', '를')}`;

/** 이/가 */
export const iGa = (word: string) => `${word}${getParticle(word, '이', '가')}`;

/** 은/는 */
export const eunNeun = (word: string) => `${word}${getParticle(word, '은', '는')}`;

/** 과/와 */
export const gwaWa = (word: string) => `${word}${getParticle(word, '과', '와')}`;

/** 으로/로 */
export const euroRo = (word: string) => `${word}${getParticle(word, '으로', '로')}`;

export const setThemeColor = (themeColor: string) => {
  document.documentElement.setAttribute('data-theme', themeColor);
};
